import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { foodApi, type FoodItem } from '../lib/api/food';
import { categoriesApi } from '../lib/api/categories';
import { getCompanyId } from '../lib/config';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MoonLoader } from './ui/moon-loader';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '../hooks/use-toast';
import { Plus, Search, Edit, Trash2, Utensils, Coffee, Pizza, ChefHat, Package, PackageX } from 'lucide-react';
import { TranslatedText } from './translated-text';
import { useTranslation } from '../hooks/useTranslation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { FoodReportsTab } from './food-reports-tab';

const categorySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
  sortOrder: z.coerce.number().int().default(0),
  isActive: z.boolean().default(true),
});

const foodItemSchema = z.object({
  categoryId: z.string().min(1, 'Category is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  defaultPrice: z.coerce.number().nonnegative('Price must be non-negative').optional(),
  isAvailable: z.boolean().default(true),
  preparationTime: z.coerce.number().int().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;
type FoodItemFormData = z.infer<typeof foodItemSchema>;

const iconOptions = [
  { value: 'Utensils', label: 'Utensils', icon: Utensils },
  { value: 'Coffee', label: 'Coffee', icon: Coffee },
  { value: 'Pizza', label: 'Pizza', icon: Pizza },
  { value: 'ChefHat', label: 'Chef Hat', icon: ChefHat },
];

const colorOptions = [
  { value: '#FF5733', label: 'Red' },
  { value: '#33FF57', label: 'Green' },
  { value: '#3357FF', label: 'Blue' },
  { value: '#F39C12', label: 'Orange' },
  { value: '#9B59B6', label: 'Purple' },
  { value: '#1ABC9C', label: 'Teal' },
];

export function FoodInventoryTab() {
  const { toast } = useToast();
  const { tSync } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isFoodItemDialogOpen, setIsFoodItemDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [editingFoodItem, setEditingFoodItem] = useState<FoodItem | null>(null);

  const categoryForm = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      icon: 'Utensils',
      color: '#FF5733',
      sortOrder: 0,
      isActive: true,
    },
  });

  const foodItemForm = useForm<FoodItemFormData>({
    resolver: zodResolver(foodItemSchema),
    defaultValues: {
      categoryId: '',
      name: '',
      description: '',
      defaultPrice: 0,
      isAvailable: true,
      preparationTime: undefined,
    },
  });

  // Fetch categories - using regular Category table (shared with products)
  const { data: allCategories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories', getCompanyId()],
    queryFn: async () => {
      const companyId = getCompanyId();
      return await categoriesApi.getCategories(companyId);
    },
  });

  // Derive Food-related category data
  const { foodParentId, categories } = React.useMemo(() => {
    if (!allCategories.length) return { foodParentId: null, categories: [] };

    // Find the "Food" parent category
    const foodKeywords = ['food', 'beverage', 'kitchen', 'restaurant', 'menu'];
    const foodParent = allCategories.find((c: any) => 
      !c.parentId && foodKeywords.some(k => c.name.toLowerCase().includes(k))
    );

    if (!foodParent) {
      // Fallback: If no specific "Food" root found, show ALL root categories
      // Use null as parent ID but return root categories as the "list"
      return { 
        foodParentId: null, 
        categories: allCategories.filter((c: any) => !c.parentId) 
      };
    }

    return {
      foodParentId: foodParent.id,
      categories: allCategories.filter((c: any) => c.parentId === foodParent.id)
    };
  }, [allCategories]);

  // Fetch food items
  const { data: foodItems = [], isLoading: foodItemsLoading } = useQuery({
    queryKey: ['food-items', selectedCategoryId, foodParentId, getCompanyId()],
    // Fetch if we have a specific food parent OR if successful fallback (root categories existing means we can show items)
    enabled: true, 
    queryFn: async () => {
      const result = await foodApi.getFoodItems(selectedCategoryId || undefined, getCompanyId());
      
      // If a specific category is selected, returns items for that category
      if (selectedCategoryId) {
        return result;
      }
      
      // Filter items logic
      if (foodParentId) {
        // If we found a Food parent, show items in Food parent + its subcategories
        const validCategoryIds = new Set([foodParentId, ...categories.map(c => c.id)]);
        return result.filter(item => validCategoryIds.has(item.categoryId));
      } else {
        // Fallback mode: Show items from the visible root categories
        const visibleCategoryIds = new Set(categories.map((c: { id: string }) => c.id));
        return result.filter(item => visibleCategoryIds.has(item.categoryId));
      }
    },
  });

  // Category mutations
  const createCategoryMutation = useMutation({
    mutationFn: (data: CategoryFormData) => {
      const categoryData = {
        name: data.name,
        description: data.description,
        icon: data.icon,
        color: data.color,
        sortOrder: data.sortOrder,
        isActive: data.isActive,
        companyId: getCompanyId(),
        parentId: foodParentId || undefined // Auto-set Food as parent
      };
      return categoriesApi.createCategory(categoryData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setIsCategoryDialogOpen(false);
      categoryForm.reset();
      toast({ title: 'Success', description: 'Category created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error?.message || 'Failed to create category', variant: 'destructive' });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoryFormData }) => categoriesApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setIsCategoryDialogOpen(false);
      setEditingCategory(null);
      categoryForm.reset();
      toast({ title: 'Success', description: 'Category updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error?.message || 'Failed to update category', variant: 'destructive' });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast({ title: 'Success', description: 'Category deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error?.message || 'Failed to delete category', variant: 'destructive' });
    },
  });

  // Food item mutations
  const createFoodItemMutation = useMutation({
    mutationFn: (data: FoodItemFormData) => {
      if (!data.categoryId) throw new Error('Category ID is required');
      if (!data.name) throw new Error('Name is required');
      return foodApi.createFoodItem({ 
        ...data, 
        name: data.name,
        categoryId: data.categoryId,
        companyId: getCompanyId() 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['food-items'] });
      setIsFoodItemDialogOpen(false);
      foodItemForm.reset();
      toast({ title: 'Success', description: 'Food item created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error?.message || 'Failed to create food item', variant: 'destructive' });
    },
  });

  const updateFoodItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FoodItemFormData }) => {
      // For updates, we might not need all fields, but if categoryId is present it must be valid
      return foodApi.updateFoodItem(id, { 
        ...data, 
        name: data.name, // Ensure strict typing if present
        categoryId: data.categoryId,
        companyId: getCompanyId() // Pass companyId for ownership check
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['food-items'] });
      setIsFoodItemDialogOpen(false);
      setEditingFoodItem(null);
      foodItemForm.reset();
      toast({ title: 'Success', description: 'Food item updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error?.message || 'Failed to update food item', variant: 'destructive' });
    },
  });

  const deleteFoodItemMutation = useMutation({
    mutationFn: (id: string) => foodApi.deleteFoodItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['food-items'] });
      toast({ title: 'Success', description: 'Food item deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error?.message || 'Failed to delete food item', variant: 'destructive' });
    },
  });

  const onCategorySubmit = (data: CategoryFormData) => {
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, data });
    } else {
      createCategoryMutation.mutate(data);
    }
  };

  const onFoodItemSubmit = (data: FoodItemFormData) => {
    if (editingFoodItem) {
      updateFoodItemMutation.mutate({ id: editingFoodItem.id, data });
    } else {
      createFoodItemMutation.mutate(data);
    }
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    categoryForm.reset({
      name: category.name,
      description: category.description || '',
      icon: category.icon || 'Utensils',
      color: category.color || '#FF5733',
      sortOrder: category.sortOrder,
      isActive: category.isActive,
    });
    setIsCategoryDialogOpen(true);
  };

  const handleDeleteCategory = (id: string) => {
    if (window.confirm(tSync('Are you sure you want to delete this category?'))) {
      deleteCategoryMutation.mutate(id);
    }
  };

  const handleEditFoodItem = (item: FoodItem) => {
    setEditingFoodItem(item);
    foodItemForm.reset({
      categoryId: item.categoryId,
      name: item.name,
      description: item.description || '',
      defaultPrice: item.defaultPrice ? Number(item.defaultPrice) : undefined,
      isAvailable: item.isAvailable,
      preparationTime: item.preparationTime || undefined,
    });
    setIsFoodItemDialogOpen(true);
  };

  const handleDeleteFoodItem = (id: string) => {
    if (window.confirm(tSync('Are you sure you want to delete this food item?'))) {
      deleteFoodItemMutation.mutate(id);
    }
  };

  const filteredFoodItems = foodItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
           {/* Section Header removed for tab view to avoid redundancy if main page has header */}
           {/* <h2 className="text-2xl font-bold"><TranslatedText text="Food & Beverage" /></h2> */}
           {/* <p className="text-muted-foreground"><TranslatedText text="Manage restaurant menu items and categories" /></p> */}
        </div>
      </div>

      <Tabs defaultValue="items" className="w-full">
        <TabsList>
          <TabsTrigger value="items"><TranslatedText text="Food Items" /></TabsTrigger>
          <TabsTrigger value="categories"><TranslatedText text="Categories" /></TabsTrigger>
          <TabsTrigger value="reports"><TranslatedText text="Reports" /></TabsTrigger>
        </TabsList>

        {/* Food Items Tab */}
        <TabsContent value="items" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={tSync("Search food items...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={selectedCategoryId || 'all'} onValueChange={(v) => setSelectedCategoryId(v === 'all' ? null : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => {
                  setEditingFoodItem(null);
                  foodItemForm.reset({
                    categoryId: selectedCategoryId || categories[0]?.id || '',
                    name: '',
                    description: '',
                    defaultPrice: 0,
                    isAvailable: true,
                  });
                  setIsFoodItemDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                <TranslatedText text="Add Food Item" />
              </Button>
            </div>
          </div>

          {foodItemsLoading ? (
            <div className="flex items-center justify-center py-12">
              <MoonLoader size="lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredFoodItems.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500"><TranslatedText text="No food items found" /></p>
                  <p className="text-sm text-gray-400"><TranslatedText text="Add your first food item to get started" /></p>
                </div>
              ) : (
                filteredFoodItems.map((item) => (
                  <Card key={item.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{item.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{item.category?.name}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditFoodItem(item)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteFoodItem(item.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {item.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-sm text-gray-500">Default Price:</span>
                          <span className="font-semibold">
                            {item.defaultPrice ? formatCurrency(item.defaultPrice) : 'Not set'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.isAvailable ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Package className="w-3 h-3" />
                              Available
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <PackageX className="w-3 h-3" />
                              Unavailable
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end mr-16">
            <Button
              onClick={() => {
                setEditingCategory(null);
                categoryForm.reset({
                  name: '',
                  description: '',
                  icon: 'Utensils',
                  color: '#FF5733',
                  sortOrder: 0,
                  isActive: true,
                });
                setIsCategoryDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              <TranslatedText text="Add Category" />
            </Button>
          </div>

          {categoriesLoading ? (
            <div className="flex items-center justify-center py-12">
              <MoonLoader size="lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <Card key={category.id} className="hover:shadow-lg transition-shadow" style={{ borderLeft: `4px solid ${category.color || '#FF5733'}` }}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        {category.icon && (
                          <div className="p-2 rounded-lg" style={{ backgroundColor: `${category.color}20` }}>
                            {iconOptions.find(i => i.value === category.icon)?.icon ? 
                              React.createElement(iconOptions.find(i => i.value === category.icon)!.icon, { className: "w-5 h-5", style: { color: category.color } }) : 
                              <Utensils className="w-5 h-5" style={{ color: category.color }} />
                            }
                          </div>
                        )}
                        <div>
                          <CardTitle>{category.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{category._count?.products || 0} items</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditCategory(category)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteCategory(category.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {category.description && (
                    <CardContent>
                      <p className="text-sm text-gray-600">{category.description}</p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <FoodReportsTab />
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? <TranslatedText text="Edit Category" /> : <TranslatedText text="Add Category" />}
            </DialogTitle>
            <DialogDescription>
              <TranslatedText text="Create or update a food category" />
            </DialogDescription>
          </DialogHeader>
          <Form {...categoryForm}>
            <form onSubmit={categoryForm.handleSubmit(onCategorySubmit)} className="space-y-4">
              <FormField
                control={categoryForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Name" /></FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Breakfast" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={categoryForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Description" /></FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Morning meals and beverages" rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={categoryForm.control}
                  name="icon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel><TranslatedText text="Icon" /></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {iconOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <div className="flex items-center gap-2">
                                {React.createElement(option.icon, { className: "w-4 h-4" })}
                                {option.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={categoryForm.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel><TranslatedText text="Color" /></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {colorOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: option.value }} />
                                {option.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
                  <TranslatedText text="Cancel" />
                </Button>
                <Button type="submit" disabled={createCategoryMutation.isPending || updateCategoryMutation.isPending}>
                  {createCategoryMutation.isPending || updateCategoryMutation.isPending ? (
                    <MoonLoader size="sm" />
                  ) : editingCategory ? (
                    <TranslatedText text="Update" />
                  ) : (
                    <TranslatedText text="Create" />
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Food Item Dialog */}
      <Dialog open={isFoodItemDialogOpen} onOpenChange={setIsFoodItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingFoodItem ? <TranslatedText text="Edit Food Item" /> : <TranslatedText text="Add Food Item" />}
            </DialogTitle>
            <DialogDescription>
              <TranslatedText text="Create or update a food item" />
            </DialogDescription>
          </DialogHeader>
          <Form {...foodItemForm}>
            <form onSubmit={foodItemForm.handleSubmit(onFoodItemSubmit)} className="space-y-4">
              <FormField
                control={foodItemForm.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Category" /></FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={foodItemForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Name" /></FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Pancakes" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={foodItemForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Description" /></FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Fluffy pancakes with syrup" rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Default Price field */}
              <FormField
                control={foodItemForm.control}
                name="defaultPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel><TranslatedText text="Default Price (Optional)" /></FormLabel>
                    <FormControl>
                      <Input {...field} type="number" step="0.01" placeholder="0.00" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={foodItemForm.control}
                name="isAvailable"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="w-4 h-4"
                      />
                    </FormControl>
                    <FormLabel className="!mt-0"><TranslatedText text="Available" /></FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsFoodItemDialogOpen(false)}>
                  <TranslatedText text="Cancel" />
                </Button>
                <Button type="submit" disabled={createFoodItemMutation.isPending || updateFoodItemMutation.isPending}>
                  {createFoodItemMutation.isPending || updateFoodItemMutation.isPending ? (
                    <MoonLoader size="sm" />
                  ) : editingFoodItem ? (
                    <TranslatedText text="Update" />
                  ) : (
                    <TranslatedText text="Create" />
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
